import { useState } from "react";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import {
  Check,
  ContentCopy,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import { useCopyFeedback } from "./useCopyFeedback";
import { detailRowStyles as styles } from "./styles";

const EM_DASH = "—";
const MASK = "••••••••••••";

// One label/value row, shared by every section card. Handles the three
// variants the spec needs (plain, mono/copyable, masked+reveal+copyable) so
// each card only has to say which one it wants.
const DetailRow = ({
  label,
  value,
  mono,
  copyable,
  masked,
  subLine,
  href,
  icon,
  hideText,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const [revealed, setRevealed] = useState(false);
  const { state: copyState, copy } = useCopyFeedback();

  const isEmpty = value === null || value === undefined || value === "";
  const display = isEmpty ? EM_DASH : String(value);

  const valueSx = [
    styles.value(colors),
    mono && styles.valueMono,
    isEmpty && styles.valueMuted(colors),
    masked && !revealed && styles.maskedDots,
  ];

  const copyTooltip =
    copyState === "copied"
      ? "Copied"
      : copyState === "error"
      ? "Couldn't copy"
      : "Copy";
  const copyIconColor =
    copyState === "copied"
      ? colors.green[400]
      : copyState === "error"
      ? colors.red[500]
      : undefined;

  return (
    <Box sx={styles.row(colors)}>
      <Box sx={styles.label(colors)}>{label}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={styles.valueGroup}>
          {icon && !isEmpty && hideText && (
            <Tooltip title={display}>
              <Box sx={{ display: "flex", flexShrink: 0 }}>{icon}</Box>
            </Tooltip>
          )}
          {icon && !isEmpty && !hideText && (
            <Box sx={{ display: "flex", flexShrink: 0 }}>{icon}</Box>
          )}
          {!hideText &&
            (href && !isEmpty ? (
              <Typography
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={display}
                sx={[...valueSx, { color: colors.blue[500] }]}
              >
                {display}
              </Typography>
            ) : (
              <Typography title={display} sx={valueSx}>
                {masked && !revealed && !isEmpty ? MASK : display}
              </Typography>
            ))}
          {masked && !isEmpty && (
            <Tooltip title={revealed ? "Hide value" : "Reveal value"}>
              <IconButton
                size="small"
                sx={styles.iconButton(colors)}
                onClick={() => setRevealed((prev) => !prev)}
              >
                {revealed ? (
                  <VisibilityOff sx={styles.icon} />
                ) : (
                  <Visibility sx={styles.icon} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {(copyable || masked) && !isEmpty && (
            <Tooltip title={copyTooltip}>
              <IconButton
                size="small"
                sx={{
                  ...styles.iconButton(colors),
                  ...(copyIconColor && { color: copyIconColor }),
                }}
                onClick={() => copy(value)}
              >
                {copyState === "copied" ? (
                  <Check sx={styles.icon} />
                ) : (
                  <ContentCopy sx={styles.icon} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {subLine && !isEmpty && (
          <Typography sx={styles.subLine(colors)}>{subLine}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default DetailRow;
